import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { getColumnsWidthStyles } from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import { Wrapper } from 'app/shared/components/loanManagement/PrimaryDataWrapper';
import { CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { getConditionalArray } from 'utils/data';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getLoanDenialReasonsByLoanIdAction } from 'store/loanApplications/actions';
import {
  selectLoanApplicationBorrowerOptions,
  selectLoanDenialReasons,
} from 'store/loanApplications/selectors';
import { getDenialReasonsAction, getHMDACodesAction } from 'store/system/actions';
import { selectHMDACodeMap } from 'store/system/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import { getLoanDenialWrapperConfig } from './LoanDenial.config';
import { COLUMNS_COUNT } from './LoanDenial.constants';
import { getLoanDenialStyles } from './LoanDenial.styles';
import LoanDenialForm from './LoanDenialForm';

const LoanDenial = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const [isEditMode, setIsEditMode] = useState(false);
  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_DENIAL));
  const borrowerOptions = useSelector(selectLoanApplicationBorrowerOptions);
  const loanDenialReasons = useSelector(selectLoanDenialReasons);
  const HMDACodeMap = useSelector(selectHMDACodeMap);

  const theme = useTheme();

  const classes = getLoanDenialStyles(theme);
  const columnsCount = borrowerOptions.length + COLUMNS_COUNT;
  const columnsStyles = useMemo(
    () => getColumnsWidthStyles(theme, { columns: columnsCount }),
    [theme, columnsCount],
  );

  const topHeaderSx = useMemo(
    () => [columnsStyles.wrapper, classes.topHeader],
    [columnsStyles.wrapper, classes.topHeader],
  );
  const sideHeaderSx = useMemo(
    () => [columnsStyles.wrapper, classes.sideHeader],
    [columnsStyles.wrapper, classes.sideHeader],
  );
  const rowDataSx = useMemo(
    () => [columnsStyles.wrapper, classes.rowData],
    [columnsStyles.wrapper, classes.rowData],
  );

  const onEdit = useCallback(() => setIsEditMode(true), []);
  const onExitEditMode = useCallback(() => setIsEditMode(false), []);

  const loanDenialWrapperConfig = useMemo(
    () => getLoanDenialWrapperConfig({ isLoading, isManageAllowed, onEdit }),
    [onEdit, isManageAllowed, isLoading],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    Promise.all([
      ...getConditionalArray(loanId, () => [
        dispatch(getLoanDenialReasonsByLoanIdAction({ loanId })),
      ]),
      dispatch(getDenialReasonsAction()),
      dispatch(getHMDACodesAction()),
    ])
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, loanId]);

  if (isEditMode) {
    return <LoanDenialForm onExitEditMode={onExitEditMode} />;
  }

  return (
    <Wrapper
      config={loanDenialWrapperConfig}
      dataTestId={UnderwritingTestIds.LOAN_DENIAL_FORM_LOAN_DENIAL_REASON_DATA_BUTTON}
      isButtonsVisible={isManageAllowed}
    >
      <Box sx={classes.container}>
        {!loanDenialReasons.length ? (
          <Typography variant="regular14">No data</Typography>
        ) : (
          <>
            <Box sx={topHeaderSx}>
              <Typography variant="regular14">Denial Reasons</Typography>
              <Typography variant="regular14">HMDA Code</Typography>
              <Typography sx={classes.cellWithCheckbox} variant="regular14">
                Credit Denial
              </Typography>
              {borrowerOptions.map(({ name, value }) => (
                <Typography key={value} sx={classes.cellWithCheckbox} variant="regular14">
                  {name}
                </Typography>
              ))}
              <Typography sx={classes.cellWithCheckbox} variant="regular14">
                Affiliate credit
              </Typography>
            </Box>

            {loanDenialReasons.map(
              ({
                denialReason: { id, name, HMDACode, isDenialDueToCredit, isAffiliate },
                selectedBorrowers,
              }) => (
                <Box key={id} sx={classes.row}>
                  <Box sx={sideHeaderSx}>
                    <Typography variant="regular14">Denial Reasons</Typography>
                    <Typography variant="regular14">HMDA Code</Typography>
                    <Typography variant="regular14">Credit Denial</Typography>
                    {borrowerOptions.map(({ name, value }) => (
                      <Typography key={value} variant="regular14">
                        {name}
                      </Typography>
                    ))}
                    <Typography variant="regular14">Affiliate credit</Typography>
                  </Box>

                  <Box data-testid={UnderwritingTestIds.getLoanDenialRowId(id)} sx={rowDataSx}>
                    <div className="cursor-default overflow-hidden">
                      <Tooltip title={name}>
                        <div className="truncate">{name}</div>
                      </Tooltip>
                    </div>
                    <div className="cursor-default overflow-hidden">
                      <Tooltip title={HMDACodeMap[HMDACode]}>
                        <div className="truncate">{HMDACodeMap[HMDACode]}</div>
                      </Tooltip>
                    </div>
                    <Box sx={classes.cellWithCheckbox}>
                      <CheckboxCDS
                        disabled
                        dataTestId={UnderwritingTestIds.getLoanDenialCreditCheckboxId(id)}
                        value={isDenialDueToCredit}
                      />
                    </Box>
                    {borrowerOptions.map(({ value }) => (
                      <Box key={value} sx={classes.cellWithCheckbox}>
                        <CheckboxCDS
                          disabled
                          dataTestId={UnderwritingTestIds.getLoanDenialBorrowerCheckboxId(
                            id,
                            value,
                          )}
                          value={selectedBorrowers.includes(value)}
                        />
                      </Box>
                    ))}
                    <Box sx={classes.cellWithCheckbox}>
                      <CheckboxCDS
                        disabled
                        dataTestId={UnderwritingTestIds.getLoanDenialAffiliateCheckboxId(id)}
                        value={isAffiliate}
                      />
                    </Box>
                  </Box>
                </Box>
              ),
            )}
          </>
        )}
      </Box>
    </Wrapper>
  );
};

LoanDenial.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(LoanDenial);
